import React, { memo, useMemo } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { Cell } from '@tanstack/react-table';
import { useSelector } from 'react-redux';

import t from 'react-translate';

import { QuantileDistribution } from 'redux/schemas/app/analytics';
import { getQuantilesDistribution } from 'redux/selectors/analytics';

import {
  danger,
  hexToRgbaString,
  yellow,
} from 'styles/global_defaults/colors';
import {
  extraLargeSpacing,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import { seafoam } from 'athena/styles/colors';

import NvTable, { Column, RowProps } from 'shared/components/nv-table';
import Spinner from 'shared/components/spinner';
import { QuestionCell, ReportColumnHeader } from './utils';

const MIN_WIDTH = extraLargeSpacing;
const TOTAL_WIDTH = 200;
const styles = css`
  .legend {
    gap: ${standardSpacing * 1.5}px;
  }

  .legend-box {
    width: ${standardSpacing}px;
    height: ${standardSpacing}px;
    flex-shrink: 0;
  }

  .low-comfort {
    background-color: ${hexToRgbaString(danger, 0.2)};
  }

  .medium-comfort {
    background-color: ${hexToRgbaString(yellow, 0.2)};
  }

  .high-comfort {
    background-color: ${seafoam};
  }

  .distribution-row {
    display: flex;
    width: ${TOTAL_WIDTH}px;
    height: 25px;
    overflow: hidden;
  }

  .segment {
    min-width: ${MIN_WIDTH}px;
    border-radius: 0;
  }
`;

const DistributionBar: React.FC<{
  low: number;
  medium: number;
  high: number;
}> = ({ low, medium, high }) => {
  const segments = [
    { level: 'low', value: low },
    { level: 'medium', value: medium },
    { level: 'high', value: high },
  ];

  const segmentIsInvalid = (low === null || low === undefined)
    && (medium === null || medium === undefined)
    && (high === null || high === undefined);

  if (segmentIsInvalid) {
    return (
      <div className='distribution-row align-items-center justify-content-center'>
        <Spinner />
      </div>
    );
  }

  const visibleSegments = segments.filter(s => s.value > 0);
  const count = visibleSegments.length;

  const baseWidth = count * MIN_WIDTH;
  const remainingWidth = Math.max(TOTAL_WIDTH - baseWidth, 0);

  const totalValue = visibleSegments.reduce((sum, s) => sum + s.value, 0);

  const segmentWidths: Record<string, number> = {};

  visibleSegments.forEach(segment => {
    const proportionalWidth = totalValue > 0
      ? (segment.value / totalValue) * remainingWidth
      : remainingWidth / count;

    segmentWidths[segment.level] = MIN_WIDTH + proportionalWidth;
  });

  return (
    <div className='distribution-row'>
      {visibleSegments.map(segment => (
        <div
          key={segment.level}
          className={`segment label text-black d-flex align-items-center justify-content-center ${segment.level}-comfort`}
          style={{ width: `${segmentWidths[segment.level]}px` }}
        >
          {`${segment.value}%`}
        </div>
      ))}
    </div>
  );
};

const DATA_MAPPING = {
  PRE_COURSE: 'preDistribution',
  POST_COURSE: 'postDistribution',
};

const DistributionCellComponent: React.FC<{
  cell: Cell<QuantileDistribution, number>;
  css: SerializedStyles
}> = memo(({ cell, css: cellStyles }) => (
  <div
    css={css`
      ${cellStyles}
      gap: ${quarterSpacing}px;
    `}
    className='py-3 pl-2 d-flex flex-column'
  >
    {['PRE_COURSE', 'POST_COURSE'].map((key) => (
      <div
        className='d-flex justify-content-between'
        key={key}
      >
        <div className='font-weight-bolder'>
          {t.COURSE_ANALYTICS.PRE_POST.REPORT.QUANTILES[key]()}
        </div>
        <DistributionBar {...cell.row.original[DATA_MAPPING[key]]} />
      </div>
    ))}
  </div>
));

const cellComponents: Record<string, React.ComponentType<any>> = {
  question: QuestionCell,
  distribution: DistributionCellComponent,
};

const QuantilesDistributionRowComponent: React.FC<RowProps<QuantileDistribution>> = ({
  row,
  className,
  getCellStyle,
}) => (
  <div css={styles} className={className}>
    {row.getVisibleCells().map((cell, index) => {
      const CellComponent = cellComponents[cell.column.id];

      return (
        <CellComponent
          css={getCellStyle(index)}
          cell={cell}
          key={cell.id}
        />
      );
    })}
  </div>
);

const QuantilesDistribution: React.FC = () => {
  const quantilesDistributionData = useSelector((state) => getQuantilesDistribution(state));
  const columns = useMemo<Column<QuantileDistribution>[]>(() => [
    {
      id: 'question',
      header: t.COURSE_ANALYTICS.PRE_POST.REPORT.AVERAGE_CHANGE.QUESTIONS(),
      gridTemplateColumn: '1fr',
      accessorFn: (row) => row.question,
    },
    {
      id: 'distribution',
      header: t.COURSE_ANALYTICS.PRE_POST.REPORT.QUANTILES.DISTRIBUTION(),
      gridTemplateColumn: '2fr',
      accessorFn: (row) => row.id,
    },
  ], []);

  if (quantilesDistributionData.length === 0) {
    return null;
  }

  return (
    <div css={styles} className='mb-6'>
      <div className='d-flex align-items-center justify-content-between mb-2'>
        <div className='course-title'>
          {t.COURSE_ANALYTICS.PRE_POST.REPORT.QUANTILES.TITLE()}
        </div>
        <div className='d-flex align-items-center legend'>
          {['LOW', 'MEDIUM', 'HIGH'].map((level) => (
            <div
              key={level}
              className='d-flex align-items-center'
            >
              <div className={`legend-box mr-1 ${level.toLowerCase()}-comfort`} />
              <span className='label text-black'>
                {t.COURSE_ANALYTICS.PRE_POST.REPORT.QUANTILES[level]()}
              </span>
            </div>
          ))}
        </div>
      </div>
      <NvTable<QuantileDistribution>
        columns={columns}
        data={quantilesDistributionData}
        RowComponent={QuantilesDistributionRowComponent}
        HeaderComponent={(props) => (
          <ReportColumnHeader
            {...props}
            height={35}
            getColumnStyle={(headerIndex) => css`
              ${props.getCellStyle(headerIndex)}
              min-width: ${headerIndex === 0 ? '560px' : 'auto'};
            `}
          />
        )}
        className='bg-white'
      />
    </div>
  );
};

export default QuantilesDistribution;
